import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// ============================================================
// UI Action protocol — 8 variants the agent can emit
// ============================================================
export type UIAction =
  | { type: 'navigate'; to: string }
  | { type: 'set_filter'; view: string; field: string; value: unknown }
  | { type: 'set_search'; view: string; value: string }
  | { type: 'execute_tool'; tool_name: string; params: Record<string, unknown>; idempotency_key: string; success_message?: string; error_message?: string }
  | { type: 'select_item'; view: string; id: string }
  | { type: 'open_detail'; view: string; id: string }
  | { type: 'highlight'; selector: string; durationMs?: number }
  | { type: 'toast'; message: string; level: 'info' | 'success' | 'warning' | 'error' }
  | { type: 'confirm'; prompt: string; onConfirmToken: string }
  | { type: 'set_active_wo'; orderId: string };

// ============================================================
// Inline Widgets — rendered inside agent messages
// ============================================================
export type AgentWidget =
  | {
      type: 'kpi_grid';
      title?: string;
      items: Array<{ label: string; value: string; delta?: string; tone?: 'success' | 'warning' | 'danger' | 'neutral' }>;
    }
  | {
      type: 'oee_gauge';
      availability: number;
      performance: number;
      quality: number;
      oee: number;
      line?: string;
    }
  | {
      type: 'nc_list';
      items: Array<{ id: string; severity: 'CRITICAL' | 'MAJOR' | 'MINOR'; title: string; line?: string; ageMin?: number }>;
    }
  | {
      type: 'wo_card';
      id: string;
      product: string;
      progress: number;
      dueAt?: string;
      status?: string;
    }
  | {
      type: 'confirm';
      prompt: string;
      confirmLabel?: string;
      cancelLabel?: string;
      token: string;
      resolved?: 'confirmed' | 'cancelled';
    }
  | {
      type: 'action_buttons';
      items: Array<{ label: string; sendText: string; variant?: 'default' | 'outline' | 'secondary' }>;
    };

// ============================================================
// Chat message
// ============================================================
export type MessageRole = 'user' | 'agent' | 'system';
export type MessageStatus = 'sending' | 'streaming' | 'complete' | 'error';

export interface MessageRecommendation {
  id: string; // REC-XX
  message: string;
  priority: 'high' | 'medium' | 'low';
  acceptText: string; // text sent on "Yes"
  shownAt: number;
}

export interface Message {
  id: string;
  role: MessageRole;
  content: string;
  timestamp: number;
  status?: MessageStatus;
  uiActions?: UIAction[];
  widgets?: AgentWidget[];
  recommendation?: MessageRecommendation | null;
}

// ============================================================
// Conversational memory
// ============================================================
export interface PendingFlow {
  id: string; // flow definition id, e.g. 'create_nc'
  collected: Record<string, unknown>;
  currentSlot: string | null;
  startedAt: number;
}

export interface AgentMemory {
  lastWorkOrder: string | null;
  lastFilter: { view: string; field: string; value: unknown } | null;
  mentionedEntities: Record<string, string>;
  pendingFlow: PendingFlow | null;
}

// ============================================================
// Agent connection status — surfaced as a coloured dot in the chat header.
// ============================================================
export type AgentStatus = 'connected' | 'processing' | 'error';

// ============================================================
// Store shape
// ============================================================
interface AgentState {
  messages: Message[];
  isTyping: boolean;
  memory: AgentMemory;
  isOpen: boolean;
  /** Coarse-grained connection status used by the header indicator. */
  status: AgentStatus;

  // Actions
  sendUserMessage: (content: string) => void;
  appendAgentMessage: (message: Partial<Message>) => string;
  updateAgentMessage: (id: string, patch: Partial<Message>) => void;
  setTyping: (value: boolean) => void;
  setStatus: (status: AgentStatus) => void;
  updateMemory: (patch: Partial<AgentMemory>) => void;
  clearHistory: () => void;
  toggleOpen: () => void;
}

const initialMemory: AgentMemory = {
  lastWorkOrder: null,
  lastFilter: null,
  mentionedEntities: {},
  pendingFlow: null,
};

const newId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

/** Trim the persisted history to the most recent N messages. */
const MAX_PERSISTED_MESSAGES = 50;
const cap = (messages: Message[]): Message[] =>
  messages.length <= MAX_PERSISTED_MESSAGES
    ? messages
    : messages.slice(messages.length - MAX_PERSISTED_MESSAGES);

export const useAgentStore = create<AgentState>()(
  persist(
    (set) => ({
      messages: [],
      isTyping: false,
      memory: initialMemory,
      isOpen: true,
      status: 'connected',

      sendUserMessage: (content) =>
        set((s) => ({
          messages: cap([
            ...s.messages,
            {
              id: newId(),
              role: 'user',
              content,
              timestamp: Date.now(),
              status: 'complete',
            },
          ]),
        })),

      appendAgentMessage: (message) => {
        const id = message.id ?? newId();
        set((s) => ({
          messages: cap([
            ...s.messages,
            {
              id,
              role: 'agent',
              content: '',
              timestamp: Date.now(),
              status: 'complete',
              ...message,
            },
          ]),
        }));
        return id;
      },

      updateAgentMessage: (id, patch) =>
        set((s) => ({
          messages: s.messages.map((m) => (m.id === id ? { ...m, ...patch } : m)),
        })),

      setTyping: (value) =>
        set((s) => ({
          isTyping: value,
          // Auto-track status while typing unless we are surfacing an error.
          status: value ? 'processing' : s.status === 'error' ? 'error' : 'connected',
        })),

      setStatus: (status) => set({ status }),

      updateMemory: (patch) =>
        set((s) => ({
          memory: { ...s.memory, ...patch },
        })),

      clearHistory: () => set({ messages: [] }),

      toggleOpen: () => set((s) => ({ isOpen: !s.isOpen })),
    }),
    {
      name: 'expai_chat_history',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ messages: state.messages }),
      // Trim on rehydrate too, so previously oversized payloads collapse to 50.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<AgentState>;
        return {
          ...current,
          ...p,
          messages: cap(((p.messages as Message[] | undefined) ?? []).slice()),
        };
      },
    },
  ),
);

// ============================================================
// Bootstrap: seed lastWorkOrder from MES active order on first load
// so the agent always has a sensible default WO context.
// ============================================================
if (typeof window !== 'undefined') {
  // Defer to next tick to avoid circular import issues at module init.
  queueMicrotask(() => {
    void (async () => {
      try {
        const mes = await import('@/stores/useMESStore');
        const activeOrderId = mes.useMESStore.getState().activeOrderId;
        const state = useAgentStore.getState();
        if (!state.memory.lastWorkOrder && activeOrderId) {
          state.updateMemory({
            lastWorkOrder: activeOrderId,
            mentionedEntities: { ...state.memory.mentionedEntities, wo: activeOrderId },
          });
        }
      } catch (err) {
        console.warn('[agentStore] bootstrap skipped', err);
      }
    })();
  });
}
